import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  LinkOff as DisconnectIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useEmailAssistant, ConnectionStatus } from '../hooks/useEmailAssistant';
import { brandColors } from '../theme';

export const EmailAssistantPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    status,
    isLoading,
    error,
    connect,
    disconnect,
    refreshConnection,
    clearError,
  } = useEmailAssistant();

  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Handle OAuth callback results
  useEffect(() => {
    const success = searchParams.get('success');
    const oauthError = searchParams.get('error');
    const email = searchParams.get('email');
    const errorDescription = searchParams.get('error_description');

    if (success === 'true' && email) {
      setSuccessMessage(`Successfully connected ${decodeURIComponent(email)}`);
      // Clear URL params
      setSearchParams({});
    } else if (oauthError) {
      // Error is already handled by the hook, but we can show additional context
      console.error('OAuth error:', oauthError, errorDescription);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleConnect = async () => {
    setIsConnecting(true);
    clearError();
    try {
      const authUrl = await connect();
      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (err) {
      console.error('Connect error:', err);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnect();
      setShowDisconnectDialog(false);
      setSuccessMessage('Email disconnected successfully');
    } catch (err) {
      console.error('Disconnect error:', err);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await refreshConnection();
      setSuccessMessage('Connection refreshed successfully');
    } catch (err) {
      console.error('Refresh error:', err);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 4, px: 3 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600, color: brandColors.indigo }}>
        Barrett Email Assistant
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Connect your Office 365 email to enable AI-assisted email workflows.
      </Typography>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Connection Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {status?.connected ? (
            <ConnectedState
              connection={status.connection!}
              onDisconnect={() => setShowDisconnectDialog(true)}
              onRefresh={handleRefresh}
              formatDate={formatDate}
            />
          ) : (
            <DisconnectedState
              onConnect={handleConnect}
              isConnecting={isConnecting}
            />
          )}
        </CardContent>
      </Card>

      {/* Privacy Information Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            Privacy & Security
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            What we access:
          </Typography>
          <List dense sx={{ mb: 2 }}>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Read your email messages" />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckIcon color="success" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="View your basic profile information" />
            </ListItem>
          </List>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            What we DON'T do:
          </Typography>
          <List dense>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CancelIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Send emails on your behalf" />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CancelIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Delete or modify your emails" />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CancelIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Access your calendar or contacts" />
            </ListItem>
            <ListItem sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CancelIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Share your data with third parties" />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            You can revoke access at any time by clicking "Disconnect" above or through your{' '}
            <a
              href="https://myapps.microsoft.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: brandColors.gold }}
            >
              Microsoft account settings
            </a>.
          </Alert>
        </CardContent>
      </Card>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={showDisconnectDialog} onClose={() => setShowDisconnectDialog(false)}>
        <DialogTitle>Disconnect Email?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will revoke the Email Assistant's access to your Office 365 mailbox.
            You can reconnect at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisconnectDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDisconnect}
            color="error"
            variant="contained"
            disabled={isDisconnecting}
            startIcon={isDisconnecting ? <CircularProgress size={20} /> : <DisconnectIcon />}
          >
            {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Connected State Component
interface ConnectedStateProps {
  connection: ConnectionStatus['connection'];
  onDisconnect: () => void;
  onRefresh: () => void;
  formatDate: (date: string | null) => string;
}

const ConnectedState: React.FC<ConnectedStateProps> = ({
  connection,
  onDisconnect,
  onRefresh,
  formatDate,
}) => {
  if (!connection) return null;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: connection.hasError ? 'warning.light' : 'success.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {connection.hasError ? (
            <WarningIcon sx={{ color: 'warning.dark' }} />
          ) : (
            <CheckIcon sx={{ color: 'success.dark' }} />
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Email Connected
            {connection.hasError && (
              <Chip label="Needs Attention" color="warning" size="small" />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {connection.email}
          </Typography>
        </Box>
      </Box>

      {connection.hasError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Connection Issue
          </Typography>
          <Typography variant="body2">
            {connection.errorMessage || 'There was a problem refreshing your access token.'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Try clicking "Refresh Connection" below, or disconnect and reconnect your email.
          </Typography>
        </Alert>
      )}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Connected On
          </Typography>
          <Typography variant="body2">
            {formatDate(connection.connectedAt)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Last Used
          </Typography>
          <Typography variant="body2">
            {formatDate(connection.lastUsedAt)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Last Token Refresh
          </Typography>
          <Typography variant="body2">
            {formatDate(connection.lastTokenRefreshAt)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Provider
          </Typography>
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {connection.provider}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
        >
          Refresh Connection
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DisconnectIcon />}
          onClick={onDisconnect}
        >
          Disconnect
        </Button>
      </Box>
    </>
  );
};

// Disconnected State Component
interface DisconnectedStateProps {
  onConnect: () => void;
  isConnecting: boolean;
}

const DisconnectedState: React.FC<DisconnectedStateProps> = ({
  onConnect,
  isConnecting,
}) => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: 'grey.200',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EmailIcon sx={{ color: 'grey.500' }} />
      </Box>
      <Box>
        <Typography variant="h6">
          Email Not Connected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Connect your Office 365 email to get started
        </Typography>
      </Box>
    </Box>

    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      By connecting your email, you authorize the Barrett Email Assistant to read your
      Office 365 mailbox for AI-assisted email workflows. Your organization's administrator
      has approved this application for use.
    </Typography>

    <Button
      variant="contained"
      size="large"
      startIcon={isConnecting ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
      onClick={onConnect}
      disabled={isConnecting}
      sx={{
        backgroundColor: brandColors.indigo,
        '&:hover': {
          backgroundColor: brandColors.gold,
        },
      }}
    >
      {isConnecting ? 'Starting Connection...' : 'Connect Microsoft Email'}
    </Button>
  </>
);

export default EmailAssistantPage;
